import { isPlaceableInterface } from '@/challenges/definition';
import { isSuppliedChallengeInterface } from '@/challenges/editing';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import type {
  IEditorModel,
  IPlaceTileModel,
  IConnectionModel,
  ITileManagerModel,
  TileTemplate,
} from '@/interfaces/models';
import { TileType } from '@/models/TileTypes';
import { CircuitType, oppositeCircuitType } from '@/schemas/common';
import type { InterfaceLinkInstanceId } from '@/types/ids';
import { SIInputMode } from '@/models/ChipModel';
import type { SingleInterfaceTileState } from '@/models/tiles/SingleInterfaceTile';
import { Signal } from '@/models/data/Signal';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import type Context from '@/context';

const createTypeTemplateId = (tileType: TileType) => `type:${tileType}`;
const PAINT_CONNECTED_WIRES_KEY = 'digitiles-paint-connected-wires';
const createInterfaceTemplateId = (linkId: InterfaceLinkInstanceId) =>
  `interface:${linkId}`;

export class PlaceTileModel implements IPlaceTileModel {
  selectedTemplateId: string | null = null;
  placementMessage: string | null = null;
  bundleInterfaceSignalId: string | undefined;
  setBundleInterfaceSignalId(signalId: string | undefined) {
    this.bundleInterfaceSignalId = signalId;
  }
  paintConnectedWires = true;
  setPaintConnectedWires(value: boolean) {
    this.paintConnectedWires = value;
    try {
      localStorage.setItem(PAINT_CONNECTED_WIRES_KEY, String(value));
    } catch {
      /* Keep the current session choice. */
    }
  }
  placementTint: string | undefined = undefined;

  setPlacementTint(color?: string) {
    if (color !== undefined && !/^#[0-9a-f]{6}$/i.test(color)) return;
    this.placementTint = color?.toLowerCase();
  }

  private previewingInterfaceLinkIds = new Set<InterfaceLinkInstanceId>();
  private reservedInterfaceLinkIds: InterfaceLinkInstanceId[] = [];
  private interactionSelection: {
    id: string | null;
    order: string[];
    structure: boolean;
  } | null = null;
  private previewTemplateId: string | null = null;

  constructor(
    public previewTileManagerModel: ITileManagerModel,
    public previewConnectionModel: IConnectionModel,
    private editorModel?: IEditorModel,
    private context?: Context,
  ) {
    try {
      this.paintConnectedWires =
        localStorage.getItem(PAINT_CONNECTED_WIRES_KEY) !== 'false';
    } catch {
      /* Missing or unavailable preferences default on. */
    }
    makeAutoObservable(this, {}, { autoBind: true });

    this.setupReactions();
  }

  selectTemplate(templateId: string) {
    this.selectedTemplateId = templateId;
  }

  reserveTemplateForPreview(): TileTemplate | null {
    const selected = this.interactionSelection
      ? this.tileTemplates.find((template) => template.id === this.previewTemplateId)
      : this.selectedTemplate;
    if (!selected) {
      return null;
    }

    const reservedIndex = this.previewingInterfaceLinkIds.size;
    const reservedLinkId = this.reservedInterfaceLinkIds[reservedIndex];
    if (reservedLinkId) {
      const reservedTemplate = this.unmatchedInterfaceTemplates.find(
        (template) =>
          template.interfaceLinkId === reservedLinkId &&
          template.destinationCircuit ===
            this.editorModel?.currentChipEditor?.editingMode,
      );
      if (reservedTemplate) {
        this.previewingInterfaceLinkIds.add(reservedLinkId);
        this.previewTemplateId = this.getNextUnmatchedTemplateId(reservedLinkId);
        return reservedTemplate;
      }
    }

    if (!selected.isUnmatchedInterface) {
      return selected;
    }

    const available = this.unmatchedInterfaceTemplates.filter(
      (template) =>
        template.interfaceLinkId &&
        template.destinationCircuit ===
          this.editorModel?.currentChipEditor?.editingMode &&
        !this.previewingInterfaceLinkIds.has(template.interfaceLinkId),
    );

    if (available.length > 0) {
      const nextTemplate =
        available.find((template) => template.id === selected.id) ?? available[0];
      const linkId = nextTemplate.interfaceLinkId as InterfaceLinkInstanceId;
      this.previewingInterfaceLinkIds.add(linkId);
      this.reservedInterfaceLinkIds.push(linkId);
      this.previewTemplateId = this.getNextUnmatchedTemplateId(linkId);
      return nextTemplate;
    }

    const fallback = this.getFallbackInterfaceTemplate();
    if (fallback) {
      this.previewTemplateId = fallback.id;
      return fallback;
    }

    return selected;
  }

  startInteraction() {
    this.interactionSelection = {
      id: this.selectedTemplateId,
      order: this.unmatchedInterfaceTemplates.map((template) => template.id),
      structure:
        this.editorModel?.currentChipEditor?.editingMode === CircuitType.ChipStructure,
    };
    this.previewTemplateId = this.selectedTemplateId;
    this.previewingInterfaceLinkIds.clear();
    this.reservedInterfaceLinkIds = [];
  }

  resetInteractionPreview() {
    this.previewingInterfaceLinkIds.clear();
    this.previewTemplateId = this.interactionSelection?.id ?? this.selectedTemplateId;
  }

  endInteraction(placedCount: number) {
    const selection = this.interactionSelection;
    if (placedCount > 0 && selection && this.selectedTemplateId === selection.id) {
      if (
        placedCount === 1 &&
        selection.structure &&
        selection.id &&
        selection.order.includes(selection.id) &&
        !this.unmatchedInterfaceTemplates.some((template) => template.id === selection.id)
      ) {
        this.selectedTemplateId = this.remainingTemplateId(selection.id, selection.order);
      } else if (placedCount > 1 || !selection.structure) {
        this.selectedTemplateId = this.previewTemplateId;
      }
    }
    this.cancelInteraction();
  }

  cancelInteraction() {
    this.previewingInterfaceLinkIds.clear();
    this.reservedInterfaceLinkIds = [];
    this.interactionSelection = null;
    this.previewTemplateId = null;
    this.ensureSelectedTemplateValid();
  }

  isTemplatePreviewing(templateId: string): boolean {
    const template = this.tileTemplates.find((entry) => entry.id === templateId);
    if (!template?.interfaceLinkId) {
      return false;
    }

    return this.previewingInterfaceLinkIds.has(template.interfaceLinkId);
  }

  bindEditor(editorModel: IEditorModel) {
    this.editorModel = editorModel;
  }

  private setupReactions() {
    autorun(() => {
      // Keep the user's selection during a gesture; only committed placement
      // may advance it. Undo/redo keep any selection that is still available.
      if (!this.interactionSelection) {
        if (this.selectedTemplateId && !this.selectedTemplate) {
          runInAction(() => this.ensureSelectedTemplateValid());
        }
      }
    });
  }

  private get sideBySidePalette() {
    return (
      !!this.context?.userPreferences?.sideBySideEnabled &&
      !this.editorModel?.currentChip?.templateType &&
      this.editorModel?.currentChip?.kind !== 'bundle-adapter' &&
      !(
        (this.context?.challengeAuthoring || this.context?.challenge) &&
        this.editorModel?.currentChip?.id === this.context.mainChip.id
      )
    );
  }

  private get allowedTileTypes() {
    if (!this.editorModel) {
      return [];
    }

    const chipEditor = this.editorModel.currentChipEditor;
    if (!chipEditor) {
      return [];
    }

    const adapter = chipEditor.chipModel.kind === 'bundle-adapter';
    const allowed = adapter
      ? [TileType.SingleInterface, TileType.Structure, TileType.BundleInterface]
      : this.sideBySidePalette
        ? [
            ...new Set([
              ...chipEditor.logicCircuitEditor.placeableTiles,
              ...chipEditor.structureCircuitEditor.placeableTiles,
            ]),
          ]
        : (chipEditor.currentCircuitEditor.placeableTiles ?? []);
    return this.context?.challenge &&
      !this.context.challengeAuthoring &&
      !(
        this.context.challenge.playerChips &&
        this.editorModel.currentChip !== this.context.mainChip
      )
      ? allowed.filter((type) => !isSuppliedChallengeInterface(type))
      : allowed;
  }

  get selectableTileTypes() {
    const allowed = this.allowedTileTypes;
    if (
      !allowed.includes(TileType.BundleInterface) ||
      this.editorModel?.currentChipEditor?.chipModel.kind === 'bundle-adapter'
    )
      return allowed;
    const hasLegacyBundle = (
      this.sideBySidePalette
        ? [
            ...this.editorModel!.currentChipEditor!.logicCircuitEditor.circuitModel
              .tileManagerModel.tiles,
            ...this.editorModel!.currentChipEditor!.structureCircuitEditor.circuitModel
              .tileManagerModel.tiles,
          ]
        : this.editorModel!.currentCircuit.tileManagerModel.tiles
    ).some((tile) => !!tile.meta.bundle);
    return hasLegacyBundle
      ? allowed
      : allowed.filter((type) => type !== TileType.BundleInterface);
  }

  get baseTileTemplates(): TileTemplate[] {
    const templates = this.unfilteredBaseTileTemplates;
    return this.selectableTileTypes.includes(TileType.BundleInterface)
      ? templates
      : templates.filter((template) => template.tileType !== TileType.BundleInterface);
  }

  private get unfilteredBaseTileTemplates(): TileTemplate[] {
    const isLogicCircuit =
      this.sideBySidePalette ||
      this.editorModel?.currentChipEditor?.editingMode === CircuitType.ChipLogic;

    return this.allowedTileTypes.flatMap((tileType): TileTemplate[] => {
      if (tileType === TileType.BundleInterface)
        return [
          {
            id: createTypeTemplateId(tileType),
            tileType,
            name: 'Bundle interface',
            state:
              this.editorModel?.currentChip.kind !== 'bundle-adapter' &&
              this.bundleInterfaceSignalId
                ? { signalId: this.bundleInterfaceSignalId }
                : {},
          },
        ];
      if (tileType === TileType.SingleInterface && isLogicCircuit) {
        return [
          { id: createTypeTemplateId(tileType), tileType, name: 'Interface (Wire)' },
          {
            id: 'type:SingleInterface:lever',
            tileType,
            name: 'Interface (Lever)',
            state: { inputMode: SIInputMode.LEVER, toggleState: Signal.LOW },
          },
          {
            id: 'type:SingleInterface:button',
            tileType,
            name: 'Interface (Button)',
            state: { inputMode: SIInputMode.BUTTON, toggleState: Signal.LOW },
          },
        ];
      }
      return [
        {
          id: createTypeTemplateId(tileType),
          tileType,
          name: tileType.startsWith('extension:')
            ? tileDefinitionRegistry.get(tileType).name
            : undefined,
        },
      ];
    });
  }

  get unmatchedInterfaceTemplates(): TileTemplate[] {
    if (!this.editorModel) {
      return [];
    }

    const chipEditor = this.editorModel.currentChipEditor;
    if (!chipEditor) {
      return [];
    }

    return (
      this.sideBySidePalette
        ? [CircuitType.ChipLogic, CircuitType.ChipStructure]
        : [chipEditor.editingMode]
    ).flatMap((type) => this.unmatchedForCircuit(type));
  }

  private unmatchedForCircuit(currentCircuitType: CircuitType): TileTemplate[] {
    const chipEditor = this.editorModel?.currentChipEditor;
    if (!chipEditor) return [];

    const otherCircuitType = oppositeCircuitType(currentCircuitType);

    if (chipEditor.chipModel.kind === 'bundle-adapter') return [];
    return chipEditor.chipModel.interfaceLinks
      .filter((link) => {
        const rootLogic =
          currentCircuitType === CircuitType.ChipLogic &&
          chipEditor.chipModel === this.context?.mainChip;
        const required =
          rootLogic &&
          (this.context?.challengeAuthoring ||
            (this.context?.challenge &&
              isPlaceableInterface(this.context.challenge, link.label)));
        return (
          (link.tiles[otherCircuitType] || required) && !link.tiles[currentCircuitType]
        );
      })
      .map((link) => {
        const otherTile = link.tiles[otherCircuitType];
        const state: Record<string, unknown> = {
          connectedInterface: {
            interfaceLinkId: link.id,
          },
        };
        state.inputMode = link.inputMode;
        state.toggleState =
          otherTile?.tileType === TileType.SingleInterface &&
          (link.inputMode === SIInputMode.LEVER || link.inputMode === SIInputMode.BUTTON)
            ? ((otherTile.state as SingleInterfaceTileState).toggleState ?? Signal.LOW)
            : Signal.LOW;
        return {
          id: this.sideBySidePalette
            ? `${createInterfaceTemplateId(link.id)}:${currentCircuitType}`
            : createInterfaceTemplateId(link.id),
          destinationCircuit: currentCircuitType,
          tileType: otherTile?.tileType ?? TileType.SingleInterface,
          state,
          interfaceLinkId: link.id,
          isUnmatchedInterface: true,
          context: {
            interfaceLink: link,
          },
        } satisfies TileTemplate;
      })
      .filter(
        (template) =>
          !this.context?.challenge ||
          this.context.challengeAuthoring ||
          (!!this.context.challenge.playerChips &&
            chipEditor.chipModel !== this.context.mainChip) ||
          !isSuppliedChallengeInterface(template.tileType) ||
          (chipEditor.chipModel === this.context.mainChip &&
            currentCircuitType === CircuitType.ChipLogic &&
            isPlaceableInterface(
              this.context.challenge,
              template.context.interfaceLink.label,
            )),
      );
  }

  get tileTemplates(): TileTemplate[] {
    const debug =
      this.editorModel?.currentChip?.logicCircuit?.logicSimulation?.debugTimers
        ?.enabled &&
      (this.sideBySidePalette ||
        this.editorModel.currentChipEditor?.editingMode === CircuitType.ChipLogic) &&
      this.editorModel.currentChip.kind !== 'bundle-adapter';
    return [
      ...this.baseTileTemplates,
      ...(debug
        ? [TileType.DebugTimerStart, TileType.DebugTimerEnd].map((tileType) => ({
            id: createTypeTemplateId(tileType),
            tileType,
            name: tileDefinitionRegistry.get(tileType).name,
          }))
        : []),
      ...this.unmatchedInterfaceTemplates,
    ];
  }

  get selectedTemplate(): TileTemplate | null {
    if (!this.selectedTemplateId) {
      return null;
    }

    // Ordinary selections do not depend on legacy bundle presence anywhere in
    // the circuit. Share the palette's template objects without evaluating that
    // whole-file visibility check after every local edit.
    const base = this.unfilteredBaseTileTemplates.find(
      (template) => template.id === this.selectedTemplateId,
    );
    if (base && base.tileType !== TileType.BundleInterface) return base;
    return (
      this.tileTemplates.find((template) => template.id === this.selectedTemplateId) ??
      null
    );
  }

  private getNextUnmatchedTemplateId(
    currentLinkId: InterfaceLinkInstanceId,
  ): string | null {
    const currentId =
      this.unmatchedInterfaceTemplates.find(
        (t) =>
          t.interfaceLinkId === currentLinkId &&
          t.destinationCircuit === this.editorModel?.currentChipEditor?.editingMode,
      )?.id ?? createInterfaceTemplateId(currentLinkId);
    const unmatched = this.unmatchedInterfaceTemplates;
    const currentIndex = unmatched.findIndex((template) => template.id === currentId);
    const nextTemplate = unmatched
      .slice(currentIndex + 1)
      .find(
        (t) => t.destinationCircuit === this.editorModel?.currentChipEditor?.editingMode,
      );

    if (nextTemplate) {
      return nextTemplate.id;
    }

    const fallback = this.getFallbackInterfaceTemplate();
    return fallback ? fallback.id : null;
  }

  private getFallbackInterfaceTemplate(): TileTemplate | null {
    return (
      this.baseTileTemplates.find(
        (template) => template.tileType === TileType.SingleInterface,
      ) ?? null
    );
  }

  private remainingTemplateId(id: string, order: string[]): string | null {
    const available = new Set(
      this.unmatchedInterfaceTemplates
        .filter(
          (t) =>
            t.destinationCircuit === this.editorModel?.currentChipEditor?.editingMode,
        )
        .map((template) => template.id),
    );
    const index = order.indexOf(id);
    if (index >= 0) {
      const next = order.slice(index + 1).find((candidate) => available.has(candidate));
      const previous = order
        .slice(0, index)
        .reverse()
        .find((candidate) => available.has(candidate));
      if (next ?? previous) return next ?? previous ?? null;
    }
    return this.getFallbackInterfaceTemplate()?.id ?? null;
  }

  private ensureSelectedTemplateValid() {
    const selected = this.selectedTemplate;
    if (selected) {
      return;
    }

    this.selectedTemplateId = this.getFallbackInterfaceTemplate()?.id ?? null;
  }
}
